import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eraser, PenLine, Check } from "lucide-react";

export default function SignaturePad({ onSubmit, disabled }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [name, setName] = useState("");
  const [hasStroke, setHasStroke] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0a0a0a";
  }, []);

  const point = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t?.clientX ?? e.clientX) - rect.left, y: (t?.clientY ?? e.clientY) - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = point(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = point(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasStroke(true);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasStroke(false);
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Informe seu nome");
    if (!hasStroke) return toast.error("Assine no quadro");
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await onSubmit({ signer_name: name.trim(), signature_data_url: dataUrl });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label>Seu nome completo</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" placeholder="Ex: Maria Silva" data-testid="sig-name" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="flex items-center gap-1.5"><PenLine size={14}/> Assine abaixo</Label>
          <button type="button" onClick={clear} className="text-xs text-neutral-500 hover:text-neutral-900 flex items-center gap-1" data-testid="sig-clear"><Eraser size={12}/> Limpar</button>
        </div>
        <canvas
          ref={canvasRef}
          className="w-full h-40 bg-neutral-50 border-2 border-dashed border-neutral-300 rounded-lg touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="sig-canvas"
        />
      </div>
      <Button onClick={submit} disabled={submitting || disabled} className="w-full h-12 rounded-md bg-neutral-900 hover:bg-neutral-800" data-testid="sig-submit">
        <Check size={16} className="mr-1.5" /> {submitting ? "Enviando…" : "Autorizar orçamento"}
      </Button>
      <p className="text-[10px] text-neutral-500 leading-relaxed">
        Ao clicar em autorizar, você concorda com o orçamento acima. Registramos data, hora e IP para fins legais.
      </p>
    </div>
  );
}
